import { Language, Service } from '@yleisradio/areena-types';
import { SearchPage } from 'components/SearchPage';
import { LocationContextProvider } from 'contexts/LocationContext';
import { ReferrerProvider } from 'contexts/ReferrerContext';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { debounce } from 'utils/debounce';
import { searchRoutes } from 'utils/routes';
import { prettyPath } from 'utils/url';
import Head from 'next/head';
import apm from 'elastic-apm-node';

export const activeServiceQueryParameterName = 'service';
export const searchQueryParameterName = 'q';

type PageProps = {
  activeService: Service;
  referrer: string | null;
  searchQuery: string | null;
  language: Language;
};

// This same component is used on parallel (Swedish) page "sok.tsx"
const Page: FunctionComponent<PageProps> = ({
  activeService,
  referrer,
  searchQuery,
  language,
}) => {
  const router = useRouter();

  const setActiveService = useCallback(
    (service: Service) => {
      const query = {
        [searchQueryParameterName]: router.query[searchQueryParameterName],
        [activeServiceQueryParameterName]: service,
      };

      void router.replace(
        {
          pathname: router.pathname,
          query,
        },
        prettyPath(router.asPath, query),
        { scroll: false }
      );
    },
    [router]
  );

  const setQuery = useMemo(
    () =>
      debounce((newQuery: string) => {
        const query = {
          [searchQueryParameterName]: newQuery,
          [activeServiceQueryParameterName]: activeService,
        };

        void router.replace(
          {
            pathname: router.pathname,
            query,
          },
          prettyPath(router.asPath, query),
          { scroll: false }
        );
      }),
    [activeService, router]
  );

  const pageTitle = language === 'sv' ? 'Yle Arenan | Sök' : 'Yle Areena | Hae';

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <ReferrerProvider referrer={referrer || undefined}>
        <LocationContextProvider>
          <SearchPage
            activeService={activeService}
            searchQuery={searchQuery}
            setActiveService={setActiveService}
            setQuery={setQuery}
          />
        </LocationContextProvider>
      </ReferrerProvider>
    </>
  );
};

export default Page;

// This same function is used on parallel (Swedish) page "sok.tsx"
export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  locale,
  query,
  req: {
    headers: { referer: referrer },
  },
  resolvedUrl,
}) => {
  const { pathname: path } = new URL(resolvedUrl, 'https://localhost/');

  apm.setTransactionName(`GET ${path}`);

  const language: Language = locale === 'sv' ? 'sv' : 'fi';
  const activeService: Service =
    query[activeServiceQueryParameterName] === 'radio' ? 'radio' : 'tv';
  const searchQuery =
    typeof query[searchQueryParameterName] === 'string'
      ? query[searchQueryParameterName]
      : null;

  if (path !== `/${searchRoutes[language]}`) {
    const redirectTo = `/${searchRoutes[language]}${
      searchQuery
        ? `?${searchQueryParameterName}=${encodeURIComponent(searchQuery)}&${activeServiceQueryParameterName}=${activeService}`
        : `?${activeServiceQueryParameterName}=${activeService}`
    }`;

    return { redirect: { destination: redirectTo, permanent: true } };
  }

  return {
    props: {
      activeService,
      referrer: referrer || null,
      searchQuery,
      language,
    },
  };
};
